/**
 * Sharing: handing a node key to someone else without handing it to the server.
 *
 * Two mechanisms, with genuinely different security properties, and the UI has
 * to be honest about both.
 *
 * ── Sharing with a person ───────────────────────────────────────────────────
 * The node key is wrapped to the recipient's ECDH public key (ECIES: ephemeral
 * key pair → ECDH → HKDF → AES-GCM). Only their private key opens it.
 *
 * The weak point is not the cryptography, it is **where the public key came
 * from**. The server serves it, so a malicious server could substitute its own
 * and read everything shared afterwards. Nothing in this protocol prevents
 * that. What the product does instead is make it detectable: the fingerprint is
 * shown, it is pinned at share time, and a later change is surfaced loudly.
 * Anyone treating this as proof against Mauvely itself should verify a
 * fingerprint out of band. Key transparency is on the backlog.
 *
 * ── Sharing by link ─────────────────────────────────────────────────────────
 * One 32-byte secret lives in the URL fragment, which browsers never send to
 * the server — not in the request line, not in Referer, not in any log. Two
 * independent HKDF branches come off it:
 *
 *   linkKey   = HKDF(secret, "…link-key.v1")    unwraps the node key, client-only
 *   linkToken = HKDF(secret, "…link-token.v1")  presented to the server as a bearer
 *
 * The server stores only SHA-256(linkToken). It can therefore enforce expiry,
 * download caps and revocation on a link whose contents it cannot read, and
 * holding the token tells it nothing about the key. That separation is the
 * whole trick.
 *
 * Password protection stacks on top, deliberately in two independent places:
 * the password is folded into the wrapping key (so ciphertext without it is
 * useless even to someone holding the link) *and* verified server-side (so the
 * wrapped key cannot simply be fetched and ground at offline).
 */

import {
  aesDecrypt,
  aesEncrypt,
  exportRaw,
  fromBase64,
  fromBase64Url,
  hkdfAesKey,
  hkdfBytes,
  importAesKey,
  randomBytes,
  sha256,
  toBase64,
  toBase64Url,
  toHex,
  utf8,
  type Bytes
} from './primitives';
import { argon2, DEFAULT_KDF_PARAMS, type KdfParams } from './argon2';
import { importPublicKey } from './keys';

export type ShareRole = 'viewer' | 'commenter' | 'editor';

/**
 * Roles are enforced by the server, not by cryptography.
 *
 * A viewer necessarily holds the key that decrypts the file — that is what
 * viewing is. Nothing stops a determined viewer keeping a copy of what they
 * were shown, and no key hierarchy could. What the server does guarantee is
 * that a viewer cannot *write*: uploads, renames and deletes check the role.
 * The share UI says this in as many words rather than implying otherwise.
 */
export const ROLE_IS_ADVISORY_FOR_READS = true;

// ── person-to-person ────────────────────────────────────────────────────────

const ECIES_AAD = utf8('mauvely.cloud.share.member.v1');

/**
 * Wrap a node key to a recipient's public key.
 *
 * Ephemeral-static ECDH: a throwaway key pair is generated per wrap, so the
 * same node shared twice produces unrelated ciphertexts and the sender's own
 * long-term key is never involved. The ephemeral public key travels alongside
 * the ciphertext, which is what the recipient needs to reconstruct the secret.
 */
export async function wrapKeyForRecipient(
  recipientPublicKeySpki: string,
  nodeKey: CryptoKey
): Promise<string> {
  const recipientPublic = await importPublicKey(recipientPublicKeySpki);
  const ephemeral = (await crypto.subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, [
    'deriveBits'
  ])) as CryptoKeyPair;

  const shared = await crypto.subtle.deriveBits(
    { name: 'ECDH', public: recipientPublic },
    ephemeral.privateKey,
    256
  );

  const wrappingKey = await hkdfAesKey(new Uint8Array(shared), 'mauvely.cloud.share.ecies.v1');
  const raw = await exportRaw(nodeKey);
  const ciphertext = await aesEncrypt(wrappingKey, raw, ECIES_AAD);
  raw.fill(0);

  const epk = new Uint8Array(await crypto.subtle.exportKey('spki', ephemeral.publicKey));
  return `${toBase64(epk)}:${ciphertext}`;
}

/** Reverse of the above, using the recipient's own private key. */
export async function unwrapKeyFromSender(
  privateKey: CryptoKey,
  wrapped: string
): Promise<CryptoKey> {
  const colon = wrapped.indexOf(':');
  if (colon < 0) throw new Error('Malformed share: expected <ephemeral-key>:<ciphertext>.');

  const ephemeralPublic = await importPublicKey(wrapped.slice(0, colon));
  const shared = await crypto.subtle.deriveBits(
    { name: 'ECDH', public: ephemeralPublic },
    privateKey,
    256
  );

  const wrappingKey = await hkdfAesKey(new Uint8Array(shared), 'mauvely.cloud.share.ecies.v1');
  const raw = await aesDecrypt(wrappingKey, wrapped.slice(colon + 1), ECIES_AAD);
  const key = await importAesKey(raw, true);
  raw.fill(0);
  return key;
}

// ── links ───────────────────────────────────────────────────────────────────

const LINK_SECRET_BYTES = 32;
const LINK_AAD = utf8('mauvely.cloud.share.link.v1');
const PASSWORD_SALT_BYTES = 16;

export interface LinkSecret {
  /** Goes in the URL fragment. Never sent to the server. */
  fragment: string;
  bytes: Bytes;
}

export const generateLinkSecret = (): LinkSecret => {
  const bytes = randomBytes(LINK_SECRET_BYTES);
  return { fragment: toBase64Url(bytes), bytes };
};

export function parseLinkSecret(fragment: string): LinkSecret {
  const bytes = fromBase64Url(fragment.replace(/^#/, ''));
  if (bytes.length !== LINK_SECRET_BYTES) throw new Error('That link is malformed.');
  return { fragment, bytes };
}

/**
 * The bearer credential the server checks.
 *
 * Derived from the same secret as the wrapping key but down a different HKDF
 * branch, so the server learns nothing usable from it. Only its SHA-256 is
 * stored, matching how website-main treats session and email tokens: a dump of
 * `share_links` cannot be replayed to open anything.
 */
export async function deriveLinkToken(secret: LinkSecret): Promise<string> {
  return toBase64Url(await hkdfBytes(secret.bytes, 'mauvely.cloud.link-token.v1'));
}

export const hashLinkToken = async (token: string): Promise<string> =>
  toHex(await sha256(fromBase64Url(token)));

/**
 * The key that wraps the node key for a link.
 *
 * When a password is set it is stretched with Argon2id and mixed into the input
 * keying material, so the wrapping key depends on both the link and the
 * password. Someone with the URL but not the password holds ciphertext they
 * cannot open — the protection is real, not a server-side check that could be
 * bypassed by talking to the API directly.
 */
async function deriveLinkKey(
  secret: LinkSecret,
  password?: { password: string; salt: Uint8Array; params: KdfParams }
): Promise<CryptoKey> {
  if (!password) return hkdfAesKey(secret.bytes, 'mauvely.cloud.link-key.v1');

  const stretched = await argon2(password.password, password.salt, password.params);
  const ikm = new Uint8Array(secret.bytes.length + stretched.length);
  ikm.set(secret.bytes, 0);
  ikm.set(stretched, secret.bytes.length);
  stretched.fill(0);

  const key = await hkdfAesKey(ikm, 'mauvely.cloud.link-key.pw.v1');
  ikm.fill(0);
  return key;
}

/**
 * The server's independent password check.
 *
 * A third HKDF branch, so it reveals nothing about the wrapping key. Its job is
 * not to protect the ciphertext — the key derivation already does that — but to
 * stop an anonymous caller pulling the wrapped key at all and brute-forcing it
 * offline, where no rate limit applies.
 */
const deriveLinkPasswordVerifier = async (
  password: string,
  salt: Uint8Array,
  params: KdfParams
): Promise<string> =>
  toHex(await hkdfBytes(await argon2(password, salt, params), 'mauvely.cloud.link-verify.v1'));

export interface CreatedLink {
  /** Append to the share URL as `#…`. The caller must never send this on. */
  fragment: string;
  /** Bearer token for the server. Safe to send; only its hash is stored. */
  token: string;
  nodeKeyWrapped: string;
  passwordSalt: string | null;
  passwordVerifier: string | null;
  kdfParams: KdfParams | null;
}

export async function createShareLink(
  nodeKey: CryptoKey,
  options: { password?: string; params?: KdfParams } = {}
): Promise<CreatedLink> {
  const secret = generateLinkSecret();
  const params = options.params ?? DEFAULT_KDF_PARAMS;

  const salt = options.password ? randomBytes(PASSWORD_SALT_BYTES) : null;
  const linkKey = await deriveLinkKey(
    secret,
    options.password && salt ? { password: options.password, salt, params } : undefined
  );

  const raw = await exportRaw(nodeKey);
  const nodeKeyWrapped = await aesEncrypt(linkKey, raw, LINK_AAD);
  raw.fill(0);

  return {
    fragment: secret.fragment,
    token: await deriveLinkToken(secret),
    nodeKeyWrapped,
    passwordSalt: salt ? toBase64(salt) : null,
    passwordVerifier:
      options.password && salt
        ? await deriveLinkPasswordVerifier(options.password, salt, params)
        : null,
    kdfParams: options.password ? params : null
  };
}

/** What the server hands back for a link, once its token and any password
 *  check have passed. */
export interface LinkPayload {
  nodeKeyWrapped: string;
  passwordSalt: string | null;
  kdfParams: KdfParams | null;
}

export async function openShareLink(
  secret: LinkSecret,
  payload: LinkPayload,
  password?: string
): Promise<CryptoKey> {
  if (payload.passwordSalt && !password) {
    throw new Error('This link needs a password.');
  }

  const linkKey = await deriveLinkKey(
    secret,
    payload.passwordSalt && password
      ? {
          password,
          salt: fromBase64(payload.passwordSalt),
          params: payload.kdfParams ?? DEFAULT_KDF_PARAMS
        }
      : undefined
  );

  const raw = await aesDecrypt(linkKey, payload.nodeKeyWrapped, LINK_AAD);
  const key = await importAesKey(raw, true);
  raw.fill(0);
  return key;
}

/** The proof a visitor sends so the server will release the wrapped key. Same
 *  value the owner computed at creation time; never the password itself. */
export const proveLinkPassword = (
  password: string,
  salt: string,
  params: KdfParams = DEFAULT_KDF_PARAMS
): Promise<string> => deriveLinkPasswordVerifier(password, fromBase64(salt), params);

/**
 * A password suggestion for the share dialog's Generate button.
 *
 * Deliberately not the recovery wordlist: this is transcribed once into a chat
 * message, so it wants to be short and unambiguous rather than long and
 * memorable. The alphabet drops the character pairs that are confused in a
 * sans-serif face, matching how website-main builds its device codes.
 */
export function suggestLinkPassword(length = 14): string {
  const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  const bytes = randomBytes(length);
  let out = '';
  // Rejection-free modulo bias is irrelevant at this alphabet size versus 256,
  // but the entropy claim should stay honest: ~5.78 bits/char, so 14 chars is
  // about 80 bits.
  for (const b of bytes) out += alphabet[b % alphabet.length];
  return out;
}
